import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

import { TranscriptionModule } from '../Recording/TranscriptionModule';

type Props = {
	note: graphql.Note;
	onRefresh: () => void;
	variant?: 'default' | 'inlineRecordButton';
};

export default function NoteTranscription({ note, onRefresh, variant }: Props) {
	const { t } = useTranslation();
	const soundRecorderV2 = useFeature(graphql.FeatureName.SoundRecorderV2);
	const [updateNote] = graphql.useUpdateNoteMutation();
	const [unlinkTranscription, { loading: unlinkLoading }] =
		graphql.useUnlinkTranscriptionFromNoteMutation();

	const onLinkTranscription = useCallback(
		async (transcriptionID: string) => {
			await updateNote({
				variables: {
					input: {
						noteID: note.id,
						transcriptionID,
						date: note.date,
					},
				},
			});
		},
		[note.id, note.date, updateNote],
	);

	const onUnlinkTranscription = useCallback(
		async (transcriptionIDFromCaller?: string) => {
			await unlinkTranscription({
				variables: {
					input: transcriptionIDFromCaller
						? { noteID: note.id, transcriptionID: transcriptionIDFromCaller }
						: { noteID: note.id },
				},
			});
		},
		[note.id, unlinkTranscription],
	);

	return (
		<TranscriptionModule
			targetID={note.id}
			targetType={graphql.TranscriptionTargetType.Note}
			transcription={note.transcription}
			onLinkTranscription={onLinkTranscription}
			onUnlinkTranscription={onUnlinkTranscription}
			unlinkLoading={unlinkLoading}
			onRefresh={onRefresh}
			canRemove={note.viewerCanUpdate}
			transcriptionLabel={t('transcription.noteTranscription')}
			recordingLabel={t('transcription.recordNote')}
			viewerCanRecord={note.viewerCanUpdate}
			disableRecording={note.isArchived}
			variant={variant}
			streamingEnabled={soundRecorderV2}
		/>
	);
}
